import { useEffect, useRef, useState } from 'react'
import type { AdminChurch } from '../../types/admin'
import { useAdminChurches } from '../../hooks/useAdmin'

interface Props {
  selected: AdminChurch | null
  onSelect: (church: AdminChurch) => void
  onClear: () => void
  placeholder?: string
  optional?: boolean
}

export default function ChurchSearchCombobox({
  selected,
  onSelect,
  onClear,
  placeholder = 'Search churches…',
  optional = false,
}: Props) {
  const [inputValue, setInputValue] = useState('')
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { data, isLoading } = useAdminChurches({ page: 1, q: query || undefined })
  const results = (data?.data ?? []).slice(0, 8)

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value
    setInputValue(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      const trimmed = value.trim()
      setQuery(trimmed)
      if (trimmed.length > 0) setIsOpen(true)
      else setIsOpen(false)
    }, 300)
  }

  function handleFocus() {
    if (query.length > 0) setIsOpen(true)
  }

  function handleSelect(church: AdminChurch) {
    onSelect(church)
    setInputValue('')
    setQuery('')
    setIsOpen(false)
  }

  function handleClear() {
    onClear()
    setInputValue('')
    setQuery('')
    setIsOpen(false)
  }

  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [])

  const baseInputClass =
    'px-3 py-2.5 rounded-lg border border-line-soft bg-bg text-[14px] font-sans w-full'

  if (selected) {
    return (
      <div className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-line-soft bg-bg">
        <span className="text-[13px] font-semibold text-good-deep">
          ✓ {selected.name}
          {(selected.city || selected.state) && (
            <span className="font-normal text-muted ml-1">
              — {[selected.city, selected.state].filter(Boolean).join(', ')}
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={handleClear}
          className="text-[12px] text-muted hover:text-ink ml-3 shrink-0 font-sans cursor-pointer border-0 bg-transparent p-0"
        >
          Change
        </button>
      </div>
    )
  }

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={baseInputClass}
        autoComplete="off"
        aria-label={optional ? 'Search churches (optional)' : 'Search churches'}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        role="combobox"
      />
      {isOpen && query.length > 0 && (
        <div
          role="listbox"
          className="absolute z-50 top-full left-0 right-0 mt-1 rounded-lg border border-line-soft bg-surface shadow-lg overflow-hidden"
        >
          {isLoading ? (
            <div className="px-3 py-2 text-[13px] text-muted">Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-[13px] text-muted">No churches found</div>
          ) : (
            <ul className="m-0 p-0 list-none">
              {results.map((church) => (
                <li key={church.id}>
                  <button
                    role="option"
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(church)}
                    className="w-full text-left px-3 py-2 text-[13px] hover:bg-s2 cursor-pointer border-0 bg-transparent font-sans"
                  >
                    <span className="font-medium text-ink">{church.name}</span>
                    {(church.city || church.state) && (
                      <span className="text-muted ml-1.5">
                        {[church.city, church.state].filter(Boolean).join(', ')}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
